import {
	Alert,
	Button,
	Card,
	Divider,
	Group,
	SimpleGrid,
	Skeleton,
	Stack,
	Tabs,
	Text,
	Title,
} from "@mantine/core";
import { IconAlertCircle, IconInfoCircle } from "@tabler/icons-react";
import { useSnapshot } from "valtio";
import { assistantUsageTexts } from "@/locales/assistant-usage";
import { i18nStore } from "@/store/i18n";
import type { ModelRateUpdate } from "@/types/ai-assistant-usage";
import { CapDailyChart } from "./cap-daily-chart";
import { CapMeterBar } from "./cap-meter-bar";
import { CapModelTable } from "./cap-model-table";
import { CapRatesEditor } from "./cap-rates-editor";
import { CapWarningBanner } from "./cap-warning-banner";
import { saveModelRate } from "./usage.api";
import type { AssistantUsageState } from "./use-assistant-usage";

interface Props {
	usage: AssistantUsageState;
	disabled: boolean;
}

/** Kartu "Pemakaian AI" (admin): meter Claude/Suara × hari/bulan, rincian model, grafik 14 hari, tarif. */
export function CapMeterCard({ usage, disabled }: Props) {
	const { lang: uiLang } = useSnapshot(i18nStore);
	const lang = uiLang === "en" ? "en" : "id";
	const t = assistantUsageTexts[lang];
	const { overview, daily, rates, loading, error, refresh } = usage;

	const onSaveRate = async (model: string, body: ModelRateUpdate) => {
		await saveModelRate(model, body);
		refresh();
	};

	return (
		<Card withBorder radius="md" p="lg" data-usage-card>
			<Stack gap="md">
				<Stack gap={2}>
					<Title order={4}>{t.title}</Title>
					<Text size="sm" c="dimmed">
						{t.intro}
					</Text>
				</Stack>
				<Alert color="blue" icon={<IconInfoCircle size={16} />} p="xs">
					<Text size="xs">{t.modeNote}</Text>
					<Text size="xs" mt={4}>
						{t.relationNote}
					</Text>
				</Alert>

				{error && (
					<Alert color="red" icon={<IconAlertCircle size={16} />}>
						<Group justify="space-between">
							<Text size="sm">{`${t.loadFailed} ${error}`}</Text>
							<Button size="xs" variant="light" onClick={refresh}>
								{t.reload}
							</Button>
						</Group>
					</Alert>
				)}

				{loading && !overview ? (
					<Skeleton height={220} radius="md" />
				) : (
					overview && (
						<>
							<CapWarningBanner
								meters={overview.meters}
								warnPercent={overview.warnPercent}
								t={t}
							/>
							<SimpleGrid cols={{ base: 1, sm: 2 }} verticalSpacing="lg">
								{overview.meters.map((m) => (
									<CapMeterBar
										key={m.id}
										meter={m}
										t={t}
										lang={lang}
										resetInSeconds={
											m.period === "day"
												? overview.resetInSeconds.day
												: overview.resetInSeconds.month
										}
									/>
								))}
							</SimpleGrid>
							<Divider />
							<Stack gap="xs">
								<Text size="sm" fw={600}>
									{t.modelsTitle}
								</Text>
								<Tabs defaultValue="day">
									<Tabs.List>
										<Tabs.Tab value="day">{t.today}</Tabs.Tab>
										<Tabs.Tab value="month">{t.month}</Tabs.Tab>
									</Tabs.List>
									{(["day", "month"] as const).map((p) => (
										<Tabs.Panel key={p} value={p} pt="sm">
											<CapModelTable
												rows={overview.models}
												period={p}
												t={t}
												lang={lang}
											/>
										</Tabs.Panel>
									))}
								</Tabs>
							</Stack>
						</>
					)
				)}
				{daily && <CapDailyChart daily={daily} t={t} />}
				{rates && (
					<CapRatesEditor
						rates={rates.rates}
						t={t}
						disabled={disabled}
						onSave={onSaveRate}
					/>
				)}
			</Stack>
		</Card>
	);
}
